import { Construction, LogOut, Monitor, Moon, PanelLeftClose, PanelLeftOpen, RefreshCw, Search, Sun } from "lucide-react";
import { type PointerEvent as ReactPointerEvent, useEffect, useState } from "react";
import { canSee, homeFor } from "../lib/access.ts";
import { ROLE_PLAIN } from "../lib/format.ts";
import { Link, navigate, usePath } from "../lib/router.tsx";
import { me, signOut } from "../lib/session.ts";
import { useSyncState } from "../lib/sync.ts";
import { inSidebar, redirectFor } from "../nav.ts";
import { useUnseenUpdates } from "../pages/po/PoUpdates.tsx"; // Order updates ---
import { SyncHealthLine } from "../pages/SyncHealth.tsx";
import { PANE_KINDS } from "../panes.tsx";
import { matchRoute, type RouteDef, routes } from "../routes.tsx";
import { CalcHint } from "../ui/CalcHint.tsx"; // Sums in number boxes ---
import { Badge, cx, EmptyState, Kbd } from "../ui/controls.tsx";
import { ErrorBoundary } from "../ui/ErrorBoundary.tsx";
import { SidePane } from "../ui/SidePane.tsx";
import { Toaster } from "../ui/toast.tsx";
import { CommandPalette, openPalette } from "./CommandPalette.tsx";
import { cycleTheme, useTheme } from "./theme.ts";

export function AppShell() {
  const path = usePath();
  const route = matchRoute(path);
  const { ready, newBuild } = useSyncState();
  const role = me()?.role;
  const allowed = !route || canSee(route, role);
  const Page = route?.page;
  // Receivers start (and only work) on the receiving screen.
  useEffect(() => {
    if (path === "/" && homeFor(role) !== "/") navigate(homeFor(role), { replace: true });
  }, [path, role]);
  // Manufacturing projects --- old addresses (Materials, Sub-assemblies) go on to where they live now
  const redirect = route && redirectFor(route, location.search);
  useEffect(() => {
    if (redirect) navigate(redirect, { replace: true });
  }, [redirect]);

  // Receivers work one screen on a tablet: a slim top bar instead of the sidebar and Ctrl+K, so the PO and its lines
  // get the whole width.
  const slim = role === "receiving";
  // The sidebar folds to a rail of icons (Ctrl+B, its button, a double-click on its edge, or dragging the edge past
  // SIDE_FOLD), and its edge drags to resize it; both remembered in this browser, so wide tables get room.
  const [rail, setRail] = useState(readRail);
  const [sideWidth, setSideWidth] = useState(readSideWidth);
  const toggleRail = () =>
    setRail((on) => {
      writeRail(!on);
      return !on;
    });
  // The width the page and the side pane share (the pane floats when it'd squeeze the page: ui/SidePane.tsx).
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const room = windowWidth - (slim ? 0 : rail ? RAIL_WIDTH : sideWidth);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey || e.key.toLowerCase() !== "b") return;
      // A text editor's own Ctrl+B (bold in the specs editor) wins.
      const target = e.target as HTMLElement | null;
      if (e.defaultPrevented || target?.isContentEditable || target?.tagName === "TEXTAREA") return;
      e.preventDefault();
      toggleRail();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div className={cx("relative flex h-full", slim && "flex-col")}>
      {slim ? (
        <TopBar />
      ) : (
        <Sidebar
          path={path}
          rail={rail}
          width={sideWidth}
          toggle={toggleRail}
          resize={(width, done) => {
            const fold = width < SIDE_FOLD;
            setRail(fold);
            if (!fold) setSideWidth(Math.max(SIDE_MIN, Math.min(SIDE_MAX, width)));
            if (done) {
              writeRail(fold);
              if (!fold) writeSideWidth(Math.max(SIDE_MIN, Math.min(SIDE_MAX, width)));
            }
          }}
        />
      )}
      <main className="flex min-h-0 min-w-0 flex-1 flex-col">
        {newBuild && (
          <div className="flex h-8 shrink-0 items-center justify-center gap-2 bg-accent text-[12px] text-white">
            A new version of HHL Ops is ready.
            <button type="button" className="underline" onClick={() => location.reload()}>
              Reload
            </button>
          </div>
        )}
        <div className="min-h-0 flex-1">
          {!ready || redirect ? null : !route ? (
            <EmptyState title="Page not found">
              <Link to="/" className="text-accent-text">
                Go home
              </Link>
            </EmptyState>
          ) : !allowed ? (
            <EmptyState title="You don't have access to this page" />
          ) : Page ? (
            <ErrorBoundary key={path}>
              <Page />
            </ErrorBoundary>
          ) : (
            <ComingSoon route={route} />
          )}
        </div>
      </main>
      {!slim && <SidePane kinds={PANE_KINDS} room={room} />}
      {!slim && <CommandPalette />}
      <Toaster />
      <CalcHint />
    </div>
  );
}

/** The receiving role's whole chrome: the app, live or not, who's signed in, theme and sign out. */
function TopBar() {
  const user = me();
  const path = usePath();
  // The pages a receiver has (Receiving; Stock counts), as tabs.
  const pages = routes.filter((r) => inSidebar(r) && canSee(r, user?.role));
  const { status } = useSyncState();
  const theme = useTheme();
  const ThemeIcon = theme === "dark" ? Moon : theme === "light" ? Sun : Monitor;
  return (
    <header className="flex h-11 shrink-0 items-center gap-3 border-b border-line bg-surface-2/60 px-4">
      <img src="/favicon.svg" alt="" className="size-5" />
      <span className="font-semibold">HHL Ops</span>
      <nav className="flex items-center gap-1">
        {pages.map((r) => (
          <Link
            key={r.path}
            to={r.path}
            className={cx(
              "rounded-md px-2 py-1 text-[13px]",
              path === r.path || path.startsWith(`${r.path}/`) ? "bg-surface font-medium" : "text-muted hover:bg-hover hover:text-text",
            )}
          >
            {r.label}
          </Link>
        ))}
      </nav>
      <span className="ml-auto flex items-center gap-1.5 text-[11px] text-faint">
        <span className={cx("size-1.5 rounded-full", status === "live" ? "bg-ok" : "bg-warn animate-pulse")} />
        {status === "live" ? "Live" : status === "loading" ? "Loading…" : "Reconnecting…"}
      </span>
      <span className="truncate text-[12px] text-muted">{user?.name || user?.email}</span>
      <button type="button" onClick={cycleTheme} className="rounded p-1.5 text-muted hover:bg-hover hover:text-text" title={`Theme: ${theme}`}>
        <ThemeIcon className="size-4" />
      </button>
      <button type="button" onClick={signOut} className="flex items-center gap-1 rounded px-2 py-1 text-[12px] text-muted hover:bg-hover hover:text-text">
        <LogOut className="size-4" /> Sign out
      </button>
    </header>
  );
}

const RAIL_KEY = "hhl-ops.sidebar";
const WIDTH_KEY = "hhl-ops.sidebar-width";
const RAIL_WIDTH = 48;
const SIDE_MIN = 180;
const SIDE_MAX = 360;
const SIDE_DEFAULT = 224;
/** Dragged narrower than this, the sidebar folds to the rail. */
const SIDE_FOLD = 140;
function readSideWidth() {
  try {
    const n = Number(localStorage.getItem(WIDTH_KEY));
    return n >= SIDE_MIN && n <= SIDE_MAX ? n : SIDE_DEFAULT;
  } catch {
    return SIDE_DEFAULT;
  }
}
function writeSideWidth(width: number) {
  try {
    localStorage.setItem(WIDTH_KEY, String(Math.round(width)));
  } catch {
    // Blocked: it lasts the tab.
  }
}
function readRail() {
  try {
    return localStorage.getItem(RAIL_KEY) === "rail";
  } catch {
    return false;
  }
}
function writeRail(on: boolean) {
  try {
    on ? localStorage.setItem(RAIL_KEY, "rail") : localStorage.removeItem(RAIL_KEY);
  } catch {
    // Blocked: it lasts the tab.
  }
}

function Sidebar({
  path,
  rail,
  width,
  toggle,
  resize,
}: {
  path: string;
  rail: boolean;
  width: number;
  toggle: () => void;
  /** A drag on its edge: the width it would be, and `done` when it's let go. */
  resize: (width: number, done: boolean) => void;
}) {
  const user = me();
  const [dragging, setDragging] = useState(false);
  const drag = (e: ReactPointerEvent<HTMLDivElement>) => {
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    setDragging(true);
    const [x0, w0] = [e.clientX, rail ? RAIL_WIDTH : width];
    let last = w0;
    const move = (ev: PointerEvent) => {
      last = w0 + ev.clientX - x0;
      resize(last, false);
    };
    const up = () => {
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", up);
      setDragging(false);
      resize(last, true);
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", up);
  };
  const visible = routes.filter((r) => inSidebar(r) && canSee(r, user?.role));
  // Settings sits at the bottom, without a heading; the other sections in nav.ts order.
  const sections = [...new Set(visible.map((r) => r.section))].filter((s) => s !== "Settings");
  const current = matchRoute(path);
  // A page off the sidebar (the Bookkeeper pack, a Settings tab) lights its section's first row (Bills, Settings).
  const stand = current && current.sidebar === false ? visible.find((v) => v.section === current.section) : undefined;
  const isActive = (r: RouteDef) => current === r || stand === r || (r.path !== "/" && path.startsWith(`${r.path}/`));
  // Order updates --- POs with vendor updates nobody at HHL has opened yet
  const news: Record<string, number> = { "/pos": useUnseenUpdates().size };
  const row = (r: RouteDef) => (
    <Link
      key={r.path}
      to={news[r.path] ? `${r.path}?status=news` : r.path}
      title={rail ? r.label : undefined}
      className={cx(
        "flex h-7 items-center gap-2 rounded-md",
        rail ? "justify-center" : "px-2",
        isActive(r) ? "bg-selected font-medium text-text" : "text-muted hover:bg-hover hover:text-text",
      )}
    >
      <span className="relative shrink-0">
        <r.icon className="size-4" />
        {rail && news[r.path] ? <span className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-accent" /> : null}
      </span>
      {!rail && <span className="flex-1 truncate">{r.label}</span>}
      {!rail && news[r.path] ? (
        <span title={`${news[r.path]} with news from the vendor`}>
          <Badge tone="accent">{news[r.path]}</Badge>
        </span>
      ) : null}
      {!rail && r.phase && !r.page && <span className="text-[10px] text-faint">P{r.phase}</span>}
    </Link>
  );
  const fold = (
    <button
      type="button"
      onClick={toggle}
      className="rounded p-1 text-faint hover:bg-hover hover:text-text"
      title={rail ? "Show the sidebar (Ctrl+B)" : "Fold the sidebar to icons (Ctrl+B)"}
    >
      {rail ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
    </button>
  );
  return (
    <nav
      style={{ width: rail ? RAIL_WIDTH : width }}
      className={cx(
        "relative flex shrink-0 flex-col border-r border-line bg-surface-2/60",
        !dragging && "transition-[width] duration-150 ease-out motion-reduce:transition-none",
      )}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: a splitter to drag, not a thematic break */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize the sidebar"
        title="Drag to resize · double-click to fold (Ctrl+B)"
        onPointerDown={drag}
        onDoubleClick={toggle}
        className={cx("absolute inset-y-0 -right-1 z-20 w-2 cursor-col-resize hover:bg-accent/40", dragging && "bg-accent/60")}
      />
      {rail ? (
        <div className="flex h-12 items-center justify-center">{fold}</div>
      ) : (
        <div className="flex h-12 items-center gap-2 pr-2 pl-4">
          <img src="/favicon.svg" alt="" className="size-5" />
          <span className="flex-1 font-semibold">HHL Ops</span>
          {fold}
        </div>
      )}
      <button
        type="button"
        onClick={openPalette}
        title={rail ? "Jump to… (Ctrl+K)" : undefined}
        className={cx(
          "mb-2 flex h-8 items-center gap-2 rounded-md border border-line bg-surface text-muted hover:border-line-strong hover:text-text",
          rail ? "mx-2 justify-center" : "mx-3 px-2.5",
        )}
      >
        <Search className="size-3.5" />
        {!rail && (
          <>
            <span className="flex-1 text-left">Jump to…</span>
            <Kbd>Ctrl K</Kbd>
          </>
        )}
      </button>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {sections.map((section) => (
          <div key={section} className={rail ? "mt-1 border-t border-line/60 pt-1 first:border-t-0" : "mt-2"}>
            {section && !rail && <div className="px-2 pt-2 pb-1 text-[11px] font-medium tracking-wide text-faint uppercase">{section}</div>}
            {visible.filter((r) => r.section === section).map(row)}
          </div>
        ))}
      </div>
      {visible.some((r) => r.section === "Settings") && <div className="px-2 pb-1">{visible.filter((r) => r.section === "Settings").map(row)}</div>}
      <SidebarFooter rail={rail} />
    </nav>
  );
}

function SidebarFooter({ rail }: { rail: boolean }) {
  const user = me();
  const { status } = useSyncState();
  const theme = useTheme();
  const [menu, setMenu] = useState(false);
  const ThemeIcon = theme === "dark" ? Moon : theme === "light" ? Sun : Monitor;
  return (
    <div className="relative border-t border-line p-2">
      <div className={cx("flex items-center gap-2 pb-1.5 text-[11px] text-faint", rail ? "justify-center" : "px-2")} title={rail ? status : undefined}>
        <span className={cx("size-1.5 rounded-full", status === "live" ? "bg-ok" : "bg-warn animate-pulse")} />
        {!rail && (status === "live" ? "Live" : status === "loading" ? "Loading…" : "Reconnecting…")}
        {!rail && status === "reconnecting" && <RefreshCw className="size-3 animate-spin" />}
      </div>
      <SyncHealthLine rail={rail} /> {/* Shopify sync health --- */}
      <button
        type="button"
        onClick={() => setMenu((m) => !m)}
        className={cx("flex w-full items-center gap-2 rounded-md py-1.5 text-left hover:bg-hover", rail ? "justify-center" : "px-2")}
        title={rail ? user?.name || user?.email : undefined}
      >
        {user?.avatar_url ? (
          <img src={user.avatar_url} alt="" className="size-6 rounded-full" referrerPolicy="no-referrer" />
        ) : (
          <span className="flex size-6 items-center justify-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent-text">
            {(user?.name || user?.email || "?")[0].toUpperCase()}
          </span>
        )}
        {!rail && (
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium">{user?.name || user?.email}</div>
            <div className="truncate text-[11px] text-faint">{user ? ROLE_PLAIN[user.role] : ""}</div>
          </div>
        )}
      </button>
      {menu && (
        <div
          className={cx("absolute bottom-full z-30 mb-1 rounded-lg border border-line bg-surface p-1 shadow-pop", rail ? "left-2 w-48" : "right-2 left-2")}
          onMouseLeave={() => setMenu(false)}
        >
          <button type="button" onClick={cycleTheme} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 hover:bg-hover">
            <ThemeIcon className="size-4" /> Theme: {theme}
          </button>
          <button type="button" onClick={signOut} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 hover:bg-hover">
            <LogOut className="size-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function ComingSoon({ route }: { route: RouteDef }) {
  return (
    <EmptyState icon={<Construction className="size-8" />} title={route.label}>
      <p>{route.blurb}</p>
      <p className="mt-3">
        <Badge>Phase {route.phase}</Badge>
      </p>
    </EmptyState>
  );
}
